<template>
  <div class="data-card-table">
    <div class="table-header">
      <div
        class="header-row"
        :style="{ gridTemplateColumns: gridTemplateColumns }"
      >
        <div
          v-for="column in columns"
          :key="column.key"
          class="header-cell"
          :class="{
            sortable: column.sortable,
            'active-sort': currentSort.key === column.key,
          }"
          @click="handleSort(column)"
        >
          <span class="header-title">{{ column.title }}</span>
          <md-icon
            v-if="column.sortable"
            class="sort-icon"
            :class="getSortIconClass(column)"
            md-src="/static/images/icon-sort-arrow.svg"
          />
        </div>
        <div class="expand-placeholder"></div>
      </div>
    </div>

    <div class="table-body">
      <div
        v-for="(item, index) in sortedData"
        :key="item.id || index"
        class="card-row"
      >
        <div
          class="card-header"
          :style="{ gridTemplateColumns: gridTemplateColumns }"
          @click="toggleExpanded(item.id || index)"
        >
          <div class="card-top-right-slot">
            <slot
              name="card-top-right"
              :item="item"
              :index="index"
            />
          </div>

          <div
            v-for="column in columns"
            :key="column.key"
            class="card-cell"
            :class="{ 'action-cell': column.type === 'action' }"
          >
            <div
              v-if="column.type === 'action'"
              class="action-buttons"
            >
              <slot
                name="actions"
                :item="item"
                :column="column"
              />
            </div>
            <div
              v-else
              class="cell-content"
              :title="getNestedValue(item, column.key)"
            >
              <slot
                :name="`cell-${column.key}`"
                :item="item"
                :value="getNestedValue(item, column.key)"
                :column="column"
              >
                {{ getNestedValue(item, column.key) }}
              </slot>
            </div>
          </div>

          <div class="expand-button">
            <md-icon
              class="expand-icon"
              :class="{ expanded: isExpanded(item.id || index) }"
            >
              expand_more
            </md-icon>
          </div>
        </div>

        <transition name="expand">
          <div
            v-if="isExpanded(item.id || index)"
            class="card-content"
          >
            <slot
              name="expanded-content"
              :item="item"
              :index="index"
            />
          </div>
        </transition>
      </div>
    </div>

    <div
      v-if="!data || data.length === 0"
      class="empty-state"
    >
      <slot name="empty-state">
        <p>Nenhum resultado encontrado.</p>
      </slot>
    </div>
  </div>
</template>

<script>
import SortColumn from './SortColumn.vue'

export default {
  name: 'DataCardTable',
  components: {
    SortColumn,
  },
  props: {
    columns: {
      type: Array,
      required: true,
      validator: columns => {
        return columns.every(col => col.key && col.title)
      },
    },
    data: {
      type: Array,
      default: () => [],
    },
    defaultSort: {
      type: Object,
      default: () => ({ key: null, direction: 'asc' }),
    },
  },
  data() {
    return {
      expandedItem: null,
      currentSort: { ...this.defaultSort },
    }
  },
  computed: {
    gridTemplateColumns() {
      const columns = this.columns
        .map(col => {
          if (col.width) return col.width
          if (col.flex) return `${col.flex}fr`
          return '1fr'
        })
        .join(' ')

      return `${columns} 50px`
    },
    sortedData() {
      if (!this.currentSort.key) return this.data

      return [...this.data].sort((a, b) => {
        const aValue = this.getNestedValue(a, this.currentSort.key)
        const bValue = this.getNestedValue(b, this.currentSort.key)

        let comparison = 0
        if (aValue > bValue) comparison = 1
        if (aValue < bValue) comparison = -1

        return this.currentSort.direction === 'desc' ? -comparison : comparison
      })
    },
  },
  methods: {
    handleSort(column) {
      if (!column.sortable) return

      if (this.currentSort.key === column.key) {
        this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.currentSort.key = column.key
        this.currentSort.direction = 'asc'
      }

      this.$emit('sort-change', { ...this.currentSort })
    },
    getSortIconClass(column) {
      if (this.currentSort.key !== column.key) return ''
      return this.currentSort.direction === 'asc' ? 'sort-asc' : 'sort-desc'
    },
    toggleExpanded(id) {
      if (this.expandedItem === id) {
        this.expandedItem = null
      } else {
        this.expandedItem = id
      }
      this.$emit('item-expanded', { id, expanded: this.isExpanded(id) })
    },
    isExpanded(id) {
      return this.expandedItem === id
    },
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => current?.[key], obj)
    },
  },
}
</script>

<style lang="scss" scoped>
.data-card-table {
  font-family: 'Exo 2', sans-serif;
  width: 100%;

  .table-header {
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 8px 8px 0 0;

    .header-row {
      display: grid;
      align-items: center;
      padding: 12px 16px;
      gap: 16px;

      .header-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        overflow: hidden;

        .header-title {
          font-size: 12px;
          font-weight: 600;
          color: #7a7a7a;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &.sortable {
          cursor: pointer;
          transition: color 0.2s ease;

          &:hover .header-title {
            color: #5a5a5a;
          }

          &.active-sort .header-title {
            color: #7f1a54;
          }
        }

        .sort-icon {
          width: 16px;
          height: 16px;
          opacity: 0.6;
          transition: all 0.2s ease;
          cursor: pointer;
          margin: 0 !important;

          :deep(.icon-sort-arrow-up),
          :deep(.icon-sort-arrow-down) {
            fill: rgb(193, 193, 193);
          }

          &.sort-asc {
            opacity: 1;

            :deep(.icon-sort-arrow-up) {
              fill: #7f1a54;
            }
          }

          &.sort-desc {
            opacity: 1;

            :deep(.icon-sort-arrow-down) {
              fill: #7f1a54;
            }
          }
        }
      }

      .expand-placeholder {
        width: 50px;
        display: flex;
        justify-content: center;
      }
    }
  }

  .table-body {
    .card-row {
      background: #fff;
      border: 1px solid #e0e0e0;
      border-top: none;
      overflow: hidden;

      &:last-child {
        border-radius: 0 0 8px 8px;
      }

      .card-header {
        display: grid;
        align-items: center;
        padding: 16px;
        gap: 16px;
        cursor: pointer;
        transition: background-color 0.2s ease;
        position: relative;

        .card-top-right-slot {
          position: absolute;
          top: -2px;
          right: 0;
          z-index: 2;
          pointer-events: none;
        }

        &:hover {
          background-color: #f9f9f9;
        }

        .card-cell {
          display: flex;
          align-items: center;
          overflow: hidden;
          min-width: 0;

          &.action-cell {
            justify-content: center;

            .action-buttons {
              display: flex;
              gap: 8px;
              align-items: center;
            }
          }

          .cell-content {
            font-size: 14px;
            color: #363636;
            line-height: 1.4;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }
        }

        .expand-button {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 50px;

          .expand-icon {
            color: #7a7a7a;
            font-size: 20px;
            transition: transform 0.3s ease;

            &.expanded {
              transform: rotate(180deg);
            }
          }
        }
      }

      .card-content {
        border-top: 1px solid #e0e0e0;
        background: #f8f9fa;
        padding: 20px;
      }
    }
  }

  .empty-state {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 40px;
    text-align: center;

    p {
      color: #7a7a7a;
      font-size: 14px;
      margin: 0;
    }
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 1000px;
  opacity: 1;
}
</style>
